import { Component } from '../../scripts/component';
import { morph } from '../../scripts/morph';
import { ThemeEvents, VariantUpdateEvent } from './events';

class LocalPickup extends Component {
  /** @type {AbortController | undefined} */
  #activeFetch;

  /** @type {Map<string, Promise<boolean | undefined>>} */
  #pickupAvailabilityCache = new Map();

  #closeOpenDialog = () => {
    const dialog = this.querySelector('dialog[ref="dialog"]');
    if (dialog instanceof HTMLDialogElement && dialog.open) {
      this.querySelector('dialog-component')?.closeDialog();
    }
  };

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.variantId) {
      this.#pickupAvailabilityCache.set(
        this.dataset.variantId,
        Promise.resolve(this.dataset.pickupAvailable === 'true'),
      );
    }

    const closestSection = this.closest(`.shopify-section, dialog`);
    const dialogComponent = this.querySelector('dialog-component');
    const dialogOpened = () => this.#updatePickupSizeAvailability();

    dialogComponent?.addEventListener('dialog:open', dialogOpened);

    /** @type {(event: VariantUpdateEvent) => void} */
    const variantUpdated = (event) => {
      if (event.detail.data.newProduct) {
        this.dataset.productUrl = event.detail.data.newProduct.url;
        this.#pickupAvailabilityCache.clear();
      }

      const variantId = event.detail.resource?.id ?? null;

      if (!variantId) {
        this.#closeOpenDialog();
        this.setAttribute('hidden', '');
        return;
      }

      const variantChanged = variantId !== this.dataset.variantId;
      const wasHidden = this.hasAttribute('hidden');

      this.removeAttribute('hidden');
      this.dataset.variantId = variantId;

      if (variantChanged || wasHidden) {
        this.#fetchAvailability(variantId);
      }
    };

    closestSection?.addEventListener(ThemeEvents.variantUpdate, variantUpdated);

    this.disconnectedCallback = () => {
      closestSection?.removeEventListener(ThemeEvents.variantUpdate, variantUpdated);
      dialogComponent?.removeEventListener('dialog:open', dialogOpened);
    };
  }

  #createAbortController() {
    if (this.#activeFetch) this.#activeFetch.abort();
    this.#activeFetch = new AbortController();
    return this.#activeFetch;
  }

  /**
   * Fetches the availability of a variant.
   * @param {string} variantId - The ID of the variant to fetch availability for.
   */
  #fetchAvailability = (variantId) => {
    if (!variantId) return;

    const abortController = this.#createAbortController();

    const url = this.dataset.productUrl;
    fetch(`${url}?variant=${variantId}&section_id=${this.dataset.sectionId}`, {
      signal: abortController.signal,
    })
      .then((response) => response.text())
      .then((text) => {
        if (abortController.signal.aborted) return;

        const html = new DOMParser().parseFromString(text, 'text/html');
        const wrapper = html.querySelector(`local-pickup[data-variant-id="${variantId}"]`);
        if (wrapper) {
          this.removeAttribute('hidden');
          this.dataset.variantId = variantId;

          const currentDialog = this.querySelector('dialog[ref="dialog"]');
          const nextDialog = wrapper.querySelector('dialog[ref="dialog"]');
          const currentTrigger = this.querySelector('.local-pickup-trigger');
          const nextTrigger = wrapper.querySelector('.local-pickup-trigger');
          const dialogIsOpen = currentDialog instanceof HTMLDialogElement && currentDialog.open;

          if (dialogIsOpen && nextDialog) {
            if (currentTrigger && nextTrigger) {
              morph(currentTrigger, nextTrigger);
            }
            morph(currentDialog, nextDialog);
          } else {
            morph(this, wrapper);
          }

          this.#pickupAvailabilityCache.set(
            variantId,
            Promise.resolve(wrapper.dataset.pickupAvailable === 'true'),
          );

          if (dialogIsOpen) this.#updatePickupSizeAvailability();
        } else {
          this.#closeOpenDialog();
          this.setAttribute('hidden', '');
        }
      })
      .catch((_e) => {
        if (abortController.signal.aborted) return;
        this.#closeOpenDialog();
        this.setAttribute('hidden', '');
      });
  };

  /**
   * Shopify Liquid only exposes store availability for the selected variant.
   * Fetch the dedicated pickup-availability section for each size when the drawer opens.
   */
  #updatePickupSizeAvailability = () => {
    const sizeFieldset = this.querySelector('fieldset[data-store-pickup-size-fieldset]');
    if (!sizeFieldset) return;

    const inputs = [...sizeFieldset.querySelectorAll('input[data-variant-id]')];
    const variantIds = [...new Set(inputs.map((input) => input.dataset.variantId).filter(Boolean))];

    const requests = variantIds.map((variantId) =>
      this.#getPickupAvailability(variantId).then((available) => {
        if (typeof available !== 'boolean') return;

        inputs
          .filter((input) => input.dataset.variantId === variantId)
          .forEach((input) => this.#setPickupSizeState(input, available));
      }),
    );

    Promise.all(requests).then(() => sizeFieldset.removeAttribute('aria-busy'));
  };

  #getPickupAvailability = (variantId) => {
    if (!this.#pickupAvailabilityCache.has(variantId)) {
      const rootUrl = window.Shopify?.routes?.root || '/';
      const request = fetch(`${rootUrl}variants/${variantId}/?section_id=pickup-availability`)
        .then((response) => (response.ok ? response.text() : undefined))
        .then((text) => {
          if (!text) return undefined;

          const html = new DOMParser().parseFromString(text, 'text/html');
          const pickup = html.querySelector(`[data-pickup-availability-status][data-variant-id="${variantId}"]`);
          if (!pickup) return undefined;

          return pickup.dataset.pickupAvailable === 'true';
        })
        .catch(() => undefined)
        .then((available) => {
          if (typeof available !== 'boolean') this.#pickupAvailabilityCache.delete(variantId);
          return available;
        });

      this.#pickupAvailabilityCache.set(variantId, request);
    }

    return this.#pickupAvailabilityCache.get(variantId);
  };

  #setPickupSizeState = (input, available) => {
    const label = input.closest('label');
    if (!label) return;

    label.classList.toggle('disabled-variant', !available);
    label.classList.toggle('text-border', !available);
    label.classList.toggle('has-[:checked]:bg-foreground', available);
    label.classList.toggle('has-[:checked]:text-background', available);
    label.classList.add('has-[:checked]:border-foreground');

    if (available) {
      input.removeAttribute('aria-disabled');
    } else {
      input.setAttribute('aria-disabled', 'true');
    }
  };
}

if (!customElements.get('local-pickup')) {
  customElements.define('local-pickup', LocalPickup);
}
